import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复您！');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pcn02qs38sj6jc7ltg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (String(serverMsg).includes('spam')) {
          setFormStatus('error');
          setFormMessage('发送失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '发送失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };
  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫网 - 品牌介绍与联系方式"
        description="了解韩漫网品牌故事与发展历程。韩漫网是专业的免费韩国漫画在线阅读平台，致力于为漫画爱好者提供优质的韩漫阅读体验。查看我们的品牌信誉与联系方式。"
        keywords="韩漫网,关于韩漫网,韩漫品牌,韩漫联系方式,免费韩漫平台介绍"
        canonicalUrl="https://hanman.readdy.ai/about"
        ogType="website"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        name="关于我们 - 韩漫网"
        description="了解韩漫网品牌故事与发展历程，查看品牌信誉与联系方式"
        url="https://hanman.readdy.ai/about"
      />

      <div className="pt-20">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]} />

        <main>
          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium mb-4">
                关于韩漫网
              </span>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-6">
                品牌介绍
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                韩漫网诞生于2024年，是一群热爱韩国漫画的年轻人创立的免费韩漫在线阅读平台。
                我们致力于为中文读者提供最新、最全的韩国漫画资源，让每一位漫画爱好者都能畅享正版韩漫阅读体验。
              </p>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-12">
                我们的使命
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-book-open-line',
                    title: '内容丰富',
                    desc: '收录500+部热门韩国漫画，涵盖恋爱、奇幻、动作、搞笑等多种类型，满足不同读者的阅读口味。',
                  },
                  {
                    icon: 'ri-flashlight-line',
                    title: '更新迅速',
                    desc: '紧跟韩国漫画更新节奏，每日同步最新章节，让读者第一时间看到心仪漫画的最新内容。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '正版授权',
                    desc: '始终坚持正版路线，所有漫画内容均来自正规授权渠道，为创作者和读者搭建健康的阅读桥梁。',
                  },
                ].map((item) => (
                  <div key={item.title} className="text-center p-6">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                      <i className={`${item.icon} text-2xl text-primary-500`}></i>
                    </div>
                    <h3 className="font-heading text-lg text-foreground-800 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-12">
                品牌信誉
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-background-50 rounded-lg border border-secondary-200/70 p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-award-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-800 mb-2">行业认可</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    韩漫网自成立以来，获得了业内多项认可。我们与多家韩国知名漫画平台建立了合作关系，
                    确保内容来源的正规性和持续性。平台累计服务超过200万读者，日均活跃用户超过15万。
                  </p>
                </div>
                <div className="bg-background-50 rounded-lg border border-secondary-200/70 p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-user-heart-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-800 mb-2">用户口碑</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    在各大应用平台和社交媒体的用户评价中，韩漫网始终保持着高分好评。
                    读者称赞我们的内容更新速度快、画质清晰、阅读体验流畅。用户满意度持续保持在95%以上。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section id="contact" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-4">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                如果您有任何问题或建议，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@hanman.readdy.ai' },
                  { icon: 'ri-customer-service-line', label: '客服热线', value: '400-888-6666' },
                  { icon: 'ri-time-line', label: '工作时间', value: '周一至周日 9:00-21:00' },
                ].map((item) => (
                  <div key={item.label} className="bg-background-50 rounded-lg border border-secondary-200/70 p-5 text-center">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className={`${item.icon} text-lg text-primary-500`}></i>
                    </div>
                    <h3 className="text-xs text-foreground-400 mb-1">{item.label}</h3>
                    <p className="text-sm text-foreground-700 font-medium">{item.value}</p>
                  </div>
                ))}
              </div>

              <form
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-secondary-200/70 p-6"
              >
                <h3 className="font-heading text-lg text-foreground-800 mb-5">发送消息</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-foreground-600 mb-1.5">姓名</label>
                    <input
                      type="text"
                      name="name"
                      className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-600 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-600 mb-1.5">消息内容</label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                  </div>
                  <div className="site-honeypot">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  {formStatus !== 'idle' && (
                    <div className={`text-sm p-3 rounded-lg ${formStatus === 'success' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'}`}>
                      {formMessage}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                </div>
              </form>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}